import { MantineColorScheme, MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import React, { useEffect, useState } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
// ‼️ import notifications styles after core package styles
import PrintLayout from "@components/layouts/PrintLayout";
import { DatesProvider } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getProductSummaries } from "api/product.service";
import { ErrorBoundary } from "components/ErrorBoundary";
import AdminLayout from "components/layouts/AdminLayout";
import MainLayout from "components/layouts/MainLayout";
import { SystemSyncer } from "components/SystemSyncer";
import { AuthProvider } from "context/AuthContext";
import { LayoutProvider } from "context/LayoutContext";
import { NavigationBridge } from "NavigationBridge";
import AboutUsPage from "pages/AboutUs";
import {
  AdminCreditJournalPage,
  AdminCreditNotePage,
  AdminDebitJournalPage,
  AdminInvoicePage,
  AdminPaymentPage,
} from "pages/admin/dataEntry";
import AdminOrderPage from "pages/admin/dataEntry/AdminOrderPage";
import { AdminDebtorEnquiry, AdminDebtorLists } from "pages/admin/debtors";
import MainAdminPage from "pages/admin/MainAdminPage";
import ManageBrandPage from "pages/admin/ManageBrandPage";
import ManageCategoryPage from "pages/admin/ManageCategoryPage";
import ManageDefaultPage from "pages/admin/ManageDefaultPage";
import ManageProductPage from "pages/admin/ManageProductPage";
import ManageUserPage from "pages/admin/ManageUserPage";
import {
  PrintCrJnl,
  PrintCrNote,
  PrintCustomerList,
  PrintCustomerOrder,
  PrintCustomerOrderHistory,
  PrintDbJnl,
  PrintDebtorBalance,
  PrintGrossProfitMargin,
  PrintOrder,
  PrintOverdueAccounts,
  PrintPayment,
  PrintProductPriceList,
  PrintSalesAnalysis,
  PrintSalesPerson,
  PrintSalesPersonByMonth,
  PrintSalesProfitMargin,
  PrintStatementAndInvoices,
  PrintStockBalanceSummary,
  PrintStockLevel,
  PrintSupplierOrder,
  PrintSupplierOrderAlt,
} from "pages/admin/print";
import MainPrintPage from "pages/admin/print/MainPrintPage";
import AdminnProductEnquiry from "pages/admin/products/AdminProductEnquiry";
import AdminProductRecall from "pages/admin/products/AdminProductRecall";
import {
  AdminCustomerOrderHistoryReportPage,
  AdminCustomerOrderReportPage,
  AdminDebtorBalanceSummary,
  AdminGrossProfitMarginPage,
  AdminOverdueAccountReportPage,
  AdminPriceListReportPage,
  AdminSalesAnalysisReportPage,
  AdminSalesPersonByMonthReportPage,
  AdminSalesPersonReportPage,
  AdminSalesProfitMarginPage,
  AdminSoHReportPage,
  AdminStockBalanceSummary,
} from "pages/admin/reports";
import AdminAgedTrialBalancePage from "pages/admin/reports/AdminAgedTrialBalancePage";
import AdminEmailStatementAndInvoices from "pages/admin/statements/AdminEmailStatementAndInvoices";
import AdminEOMPage from "pages/admin/statements/AdminEOMPage";
import AdminEOYPage from "pages/admin/statements/AdminEOYPage";
import AdminSupplierOrderPage from "pages/admin/suppliers/AdminSupplierOrderPage";
import AdminSupplierPage from "pages/admin/suppliers/AdminSupplierPage";
import ApplicationError from "pages/ApplicationError";
import ChangePasswordPage from "pages/ChangePasswordPage";
import BackOrderPage from "pages/customers/BackOrderPage";
import OverviewPage from "pages/customers/OverviewPage";
import Dashboard from "pages/Dashboard";
import GlobalAdmin from "pages/GlobalAdmin";
import Home from "pages/Home";
import MyAccountsPage from "pages/MyAccounts";
import NotAuthorized from "pages/NotAuthorized";
import Playpen from "pages/Playpen";
import ProductDetails from "pages/ProductDetails";
import ProductsPage from "pages/Products";
import ShoppingCartPage from "pages/ShoppingCartPage";
import { Navigate, useLocation } from "react-router-dom";
import { useProductStore } from "stores/useProductStore";
import { useSessionStore } from "stores/userSessionStore";
import "./App.css";
import ContactUsPage from "./pages/ContactUs";
import "./styles/global.scss";

const LayoutWrapper = ({ children }: { children: React.ReactNode }) => {
  //const selectedSystem = useSessionStore((s) => s.selectedSystem);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPrintRoute = location.pathname.startsWith("/print");
  return isAdminRoute ? (
    <AdminLayout>{children}</AdminLayout>
  ) : isPrintRoute ? (
    <PrintLayout>{children}</PrintLayout>
  ) : (
    <MainLayout>{children}</MainLayout>
  );
};

const App = () => {
  const [defaultColorScheme, setDefaultColorScheme] = useState<
    MantineColorScheme | undefined
  >("light");
  useProductStore((s) => s.productSummaries.items); // preload productSummaries

  // Admin route guard
  // This component checks if the user is an admin before rendering the children components
  // If not an admin, it redirects to a "not authorized" page.
  const AdminRoute = ({ children }: { children: React.JSX.Element }) => {
    const { isStaff, isLoggedIn, hydrated, sessionVerified } =
      useSessionStore();
    if (!hydrated) return null;
    if (!sessionVerified) return null;
    if (!isLoggedIn) return <Navigate to="/" replace />;

    return isStaff ? children : <Navigate to="/not-authorized" replace />;
  };
  const GlobalAdminRoute = ({ children }: { children: React.JSX.Element }) => {
    const { hydrated, isGlobalAdmin, isLoggedIn, sessionVerified } =
      useSessionStore();
    if (!hydrated) return null;
    if (!sessionVerified) return null;
    if (!isLoggedIn) return <Navigate to="/" replace />;

    return isGlobalAdmin ? children : <Navigate to="/not-authorized" replace />;
  };
  // Authenticated route guard
  // This component checks if the user is logged in before rendering the children components
  // If not logged in, it redirects to a "not authorized" page.
  // This is useful for protecting routes that require user authentication.
  const AuthenticatedRoute = ({
    children,
  }: {
    children: React.JSX.Element;
  }) => {
    const { hydrated, isLoggedIn, sessionVerified } = useSessionStore();
    if (!hydrated) return null;
    if (!sessionVerified) return null;

    return isLoggedIn ? children : <Navigate to="/not-authorized" replace />;
  };

  useEffect(() => {
    /** What It Does
- Zustand’s .persist.hasHydrated() returns true once the store has been rehydrated from persisted storage (like localStorage).
- By checking it, you're ensuring you don’t fetch or manipulate state until persistence is finished loading.
- Without it, you might overwrite persisted state with empty values during initial render.

✅ When to Keep It
Keep it if you’re using Zustand’s persistence middleware (createJSONStorage, persist, etc.) and want to:
- Prevent double-fetching or state wipe on app boot
- Wait until data from localStorage is loaded before fetching fresh data
Example scenario:
You persist productSummaries, and want to refetch only if the store is unpopulated — but don’t want to do that check until the persisted data is available.

❌ When It’s Safe to Remove
If:
- You’re not using Zustand persistence,
- Or your app logic doesn’t depend on restored values,
- Or you've built safer conditional fetches (like checking productSummaries.items.length after hydration),
Then it can go.
 */
    if (!useProductStore.persist.hasHydrated()) return; // TODO : may not need this line

    const fetchProductSummaries = async () => {
      try {
        const resp = await safeApiCall(getProductSummaries("summary"));
        useProductStore
          .getState()
          .setProductSummaries(resp?.data ?? { items: [] });
      } catch (error) {
        logError(error);
      }
    };
    // Start allProducts fetch after initial paint
    setTimeout(() => {
      fetchProductSummaries(); // Store in state/store, avoid UI dependency
    }, 0);
  }, []);

  return (
    <MantineProvider
      defaultColorScheme={defaultColorScheme} // Set the default color scheme"
      withCssVariables={true} // Enable CSS variables for Mantine
      withStaticClasses={true} // Enable static classes for Mantine
      theme={{
        respectReducedMotion: true,
        other: {
          notificationZIndex: 3000, // custom key
        },
        components: {
          Modal: {
            defaultProps: {
              zIndex: 9999,
            },
          },
        },
      }}
    >
      <AuthProvider>
        <DatesProvider settings={{ locale: "en" }}>
          <Router>
            {/* Wrap the application in AuthProvider for authentication context management */}
            <SystemSyncer />
            <NavigationBridge />
            <ErrorBoundary>
              {/* Notifications component for displaying notifications */}
              <LayoutProvider>
                <Notifications zIndex={3000} />

                <LayoutWrapper>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route
                      path="/print"
                      element={
                        <AdminRoute>
                          <MainPrintPage />
                        </AdminRoute>
                      }
                    >
                      <Route
                        index
                        path="orders"
                        element={
                          <AdminRoute>
                            <PrintOrder />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="statement-invoices"
                        element={
                          <AdminRoute>
                            <PrintStatementAndInvoices />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="payment"
                        element={
                          <AdminRoute>
                            <PrintPayment />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="crjnl"
                        element={
                          <AdminRoute>
                            <PrintCrJnl />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="dbjnl"
                        element={
                          <AdminRoute>
                            <PrintDbJnl />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="crnote"
                        element={
                          <AdminRoute>
                            <PrintCrNote />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="supplier-order"
                        element={
                          <AdminRoute>
                            <PrintSupplierOrder />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="supplier-order-alt"
                        element={
                          <AdminRoute>
                            <PrintSupplierOrderAlt />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="stock-on-hand"
                        element={
                          <AdminRoute>
                            <PrintStockLevel />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="debtor-balance"
                        element={
                          <AdminRoute>
                            <PrintDebtorBalance />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="debtor-lists"
                        element={
                          <AdminRoute>
                            <PrintCustomerList />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="cust-order-history"
                        element={
                          <AdminRoute>
                            <PrintCustomerOrderHistory />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="cust-orders"
                        element={
                          <AdminRoute>
                            <PrintCustomerOrder />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="balance-summary"
                        element={
                          <AdminRoute>
                            <PrintStockBalanceSummary />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="price-list"
                        element={
                          <AdminRoute>
                            <PrintProductPriceList />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="sales-person"
                        element={
                          <AdminRoute>
                            <PrintSalesPerson />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="gross-profit-margins"
                        element={
                          <AdminRoute>
                            <PrintGrossProfitMargin />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="sales-person-by-month"
                        element={
                          <AdminRoute>
                            <PrintSalesPersonByMonth />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="overdue-accounts"
                        element={
                          <AdminRoute>
                            <PrintOverdueAccounts />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="sales-profit-margins"
                        element={
                          <AdminRoute>
                            <PrintSalesProfitMargin />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="sales-analysis"
                        element={
                          <AdminRoute>
                            <PrintSalesAnalysis />
                          </AdminRoute>
                        }
                      />
                    </Route>
                    <Route
                      path="/admin"
                      element={
                        <AuthenticatedRoute>
                          <MainAdminPage />
                        </AuthenticatedRoute>
                      }
                    >
                      <Route
                        index
                        path="overdue-accounts"
                        element={
                          <AdminRoute>
                            <AdminOverdueAccountReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path=""
                        element={
                          <AdminRoute>
                            <AdminDebtorEnquiry />
                          </AdminRoute>
                        }
                      />
                      <Route
                        index
                        path="enquire-debtors"
                        element={
                          <AdminRoute>
                            <AdminDebtorEnquiry />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="debtor-lists"
                        element={
                          <AdminRoute>
                            <AdminDebtorLists />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="debtor-balance"
                        element={
                          <AdminRoute>
                            <AdminDebtorBalanceSummary />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="stock-balance"
                        element={
                          <AdminRoute>
                            <AdminStockBalanceSummary />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="orders"
                        element={
                          <AdminRoute>
                            <AdminOrderPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="invoices"
                        element={
                          <AdminRoute>
                            <AdminInvoicePage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="payments"
                        element={
                          <AdminRoute>
                            <AdminPaymentPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="credit-notes"
                        element={
                          <AdminRoute>
                            <AdminCreditNotePage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="credit-journals"
                        element={
                          <AdminRoute>
                            <AdminCreditJournalPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="debit-journals"
                        element={
                          <AdminRoute>
                            <AdminDebitJournalPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="cust-order-history"
                        element={
                          <AdminRoute>
                            <AdminCustomerOrderHistoryReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="sales-analysis"
                        element={
                          <AdminRoute>
                            <AdminSalesAnalysisReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="sales-profit-margins"
                        element={
                          <AdminRoute>
                            <AdminSalesProfitMarginPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="customer-orders"
                        element={
                          <AdminRoute>
                            <AdminCustomerOrderReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="price-list"
                        element={
                          <AdminRoute>
                            <AdminPriceListReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="stock-on-hand"
                        element={
                          <AdminRoute>
                            <AdminSoHReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="sales-person"
                        element={
                          <AdminRoute>
                            <AdminSalesPersonReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="sales-person-by-month"
                        element={
                          <AdminRoute>
                            <AdminSalesPersonByMonthReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="overdue-account"
                        element={
                          <AdminRoute>
                            <AdminOverdueAccountReportPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="statement-invoices"
                        element={
                          <AdminRoute>
                            <AdminEmailStatementAndInvoices />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="eom"
                        element={
                          <AdminRoute>
                            <AdminEOMPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="eoy"
                        element={
                          <AdminRoute>
                            <AdminEOYPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="aged-trial-balance"
                        element={
                          <AdminRoute>
                            <AdminAgedTrialBalancePage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="statements"
                        element={
                          <AdminRoute>
                            <ManageDefaultPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="enquire-products"
                        element={
                          <AdminRoute>
                            <AdminnProductEnquiry />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="product-recall"
                        element={
                          <AdminRoute>
                            <AdminProductRecall />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="gross-profit-margins"
                        element={
                          <AdminRoute>
                            <AdminGrossProfitMarginPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="enquire-suppliers"
                        element={
                          <AdminRoute>
                            <AdminSupplierPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="supplier-orders"
                        element={
                          <AdminRoute>
                            <AdminSupplierOrderPage />
                          </AdminRoute>
                        }
                      />
                    </Route>
                    <Route path="/products" element={<ProductsPage />} />
                    <Route
                      path="/about-us"
                      element={<AboutUsPage title="about-us2" />}
                    />
                    <Route path="/contact-us" element={<ContactUsPage />} />
                    <Route path="/not-authorized" element={<NotAuthorized />} />
                    <Route
                      path="/application-error"
                      element={<ApplicationError />}
                    />
                    <Route
                      path="/my-accounts"
                      element={
                        <AuthenticatedRoute>
                          <MyAccountsPage />
                        </AuthenticatedRoute>
                      }
                    >
                      <Route
                        path="global-admin"
                        element={
                          <GlobalAdminRoute>
                            <GlobalAdmin />
                          </GlobalAdminRoute>
                        }
                      />
                      <Route
                        index
                        element={
                          <AuthenticatedRoute>
                            <OverviewPage />
                          </AuthenticatedRoute>
                        }
                      />
                      <Route
                        path="back-orders"
                        element={
                          <AuthenticatedRoute>
                            <BackOrderPage />
                          </AuthenticatedRoute>
                        }
                      />
                      <Route
                        path="change-password"
                        element={
                          <AuthenticatedRoute>
                            <ChangePasswordPage />
                          </AuthenticatedRoute>
                        }
                      />
                      <Route
                        path="manage-brands"
                        element={
                          <AdminRoute>
                            <ManageBrandPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="manage-categories"
                        element={
                          <AdminRoute>
                            <ManageCategoryPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="manage-products"
                        element={
                          <AdminRoute>
                            <ManageProductPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="manage-users"
                        element={
                          <AdminRoute>
                            <ManageUserPage />
                          </AdminRoute>
                        }
                      />
                    </Route>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route
                      path="/playpen"
                      element={
                        <AuthenticatedRoute>
                          <Playpen />
                        </AuthenticatedRoute>
                      }
                    />
                    <Route
                      path="/products/details/:productCode"
                      element={<ProductDetails />}
                    />
                    <Route
                      path="/cart"
                      element={
                        <AuthenticatedRoute>
                          <ShoppingCartPage />
                        </AuthenticatedRoute>
                      }
                    />
                  </Routes>
                </LayoutWrapper>
              </LayoutProvider>
            </ErrorBoundary>
          </Router>
        </DatesProvider>
      </AuthProvider>
      {/* ✅ Global print container */}
      {/* <div id="print-container" style={{ display: "none" }} /> */}
    </MantineProvider>
  );
};

export default App;
